import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Panel } from "components/panels";
import TwoColumnLayout from "layout/TwoColumnLayout";
import React from "react";
import CategoryRow from "./CategoryRow";
import CategoryRowEdit from "./CategoryRowEdit";
import { ButtonPrimary } from "components/Atoms/Button";
import { useLoaderData } from "react-router";
import { compareLanguages } from "utilities/translationUtils";

const ArticleCategoryListApp = () => {
  const viewModel = useLoaderData<server.viewmodel.ArticleCategoryListFrontModel>();

  const queryKey = ["allCategories", viewModel.section];
  const categoriesQuery = useQuery({
    queryFn: () => window.CoreApi.ArticleCategory.Paginated({
      page: 1,
      perPage: 999,
    }, viewModel.section).then(r => r.data),
    queryKey
  });
  const queryClient = useQueryClient();

  const [editingCategoryId, setEditingCategoryId] = React.useState<number | undefined>(undefined);
  const [isCreatingNewCategory, setIsCreatingNewCategory] = React.useState<boolean>(false);

  if (categoriesQuery.isLoading) {
    return <TwoColumnLayout>
      <TwoColumnLayout.LeftColumn>
        <Panel panelClassName="s1_listview-panel" title={viewModel.pageTitle} headerSize="h1" isLoading></Panel>
      </TwoColumnLayout.LeftColumn>
    </TwoColumnLayout>
  }

  const sortedLanguages = [...viewModel.availableLanguages].sort((a, b) => compareLanguages(a.value, b.value));

  const categories = categoriesQuery.data.data ?? [];

  const onEdit = (category: server.dto.ArticleCategory) => {
    setEditingCategoryId(category.articleCategoryId);
  }

  const onCancelEdit = () => {
    setEditingCategoryId(undefined);
  }

  const onEditSave = async (translations: server.dto.SaveArticleCategoryTranslationInstruction[]) => {
    await window.CoreApi.ArticleCategory.Save({
      articleCategoryId: editingCategoryId,
      translations
    });

    queryClient.invalidateQueries(queryKey);
    setEditingCategoryId(undefined);
  }

  const onCreateSave = async (translations: server.dto.SaveArticleCategoryTranslationInstruction[]) => {
    const res = await window.CoreApi.ArticleCategory.Create(viewModel.section);
    await window.CoreApi.ArticleCategory.Save({
      articleCategoryId: res.data.data.articleCategoryId,
      translations
    });

    queryClient.invalidateQueries(queryKey);
    setIsCreatingNewCategory(false);
  }

  const onDeleteClicked = (category: server.dto.ArticleCategory) => {
    window.displayDeleteConfirmationModal({
      onDeleteHandler: async () => {
        await window.CoreApi.ArticleCategory.Delete(category.articleCategoryId);
        queryClient.invalidateQueries(queryKey);
      }
    });
  }

  return <TwoColumnLayout>
    <TwoColumnLayout.LeftColumn>
      <Panel title={viewModel.pageTitle} headerSize="h1">
        <table className="s1_table list">
          <thead>
            <tr>
              {sortedLanguages.map(lang => <th key={lang.value}>{lang.localizedName}</th>)}
              <th></th>
            </tr>
          </thead>
          <tbody>
            {categories.map(category => editingCategoryId === category.articleCategoryId
              ? <CategoryRowEdit key={category.articleCategoryId} category={category} allLanguages={viewModel.availableLanguages} onCancel={onCancelEdit} onSave={onEditSave} />
              : <CategoryRow key={category.articleCategoryId} category={category} allLanguages={viewModel.availableLanguages} onEdit={onEdit} onDelete={onDeleteClicked} />)}
            {isCreatingNewCategory && <CategoryRowEdit allLanguages={viewModel.availableLanguages} onCancel={() => setIsCreatingNewCategory(false)} onSave={onCreateSave} />}
          </tbody>
        </table>
        <div className="flex justify-center margin-top">
          <ButtonPrimary label={RESX.Buttons.btnNew} onClick={() => setIsCreatingNewCategory(true)} />
        </div>
      </Panel>
    </TwoColumnLayout.LeftColumn>
  </TwoColumnLayout>
}

export default ArticleCategoryListApp;